/**
 * 按这本书的背景生成定制大纲（P2-2）：先出方案、销售勾选、再应用。
 *
 * 模型只提议、不落库：销售逐条看、勾选，点「应用所选」才生效；服务端会再按当前大纲逐条校验
 * （已聊过的题 / 用户自己加的题 / 有聊过内容的话题不会被动）。被跳过的条目会在这里列出原因。
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Input, List, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import {
  PROPOSE_ADVICE_MAX_LENGTH,
  applyAdvisorOutline,
  proposeAdvisorOutline,
  type ApplyOutlineResult,
  type OutlineProposal,
  type ProposedChange,
} from '../../../api/advisor';
import { KIND_META, describeChange, selectedOps, summarizeApply } from './outline-proposal';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const errText = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

function ChangeRow({ change, checked, onToggle }: { change: ProposedChange; checked: boolean; onToggle: () => void }) {
  const view = describeChange(change);
  const meta = KIND_META[change.op.kind];
  return (
    <List.Item data-change-id={change.id} data-kind={change.op.kind}>
      <Space align="start" style={{ width: '100%' }}>
        <Checkbox checked={checked} onChange={onToggle} aria-label={`应用：${meta.label}`} />
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Space size={6} wrap>
            <Tag color={meta.color}>{meta.label}</Tag>
            {view.note && <Text type="secondary">{view.note}</Text>}
          </Space>
          {view.before && (
            <Text type="secondary" delete>
              {view.before}
            </Text>
          )}
          <Text strong={change.op.kind === 'add_topic'} delete={change.op.kind === 'remove_question'}>
            {view.title}
          </Text>
          {view.questions && (
            <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>
              {view.questions.map((q) => (
                <li key={q}>
                  <Text>{q}</Text>
                </li>
              ))}
            </ul>
          )}
          {view.reason && <Text type="secondary">依据：{view.reason}</Text>}
        </Space>
      </Space>
    </List.Item>
  );
}

export function OutlineProposalModal({
  open,
  customerId,
  workId,
  onClose,
  onApplied,
}: {
  open: boolean;
  customerId: string;
  workId: string;
  onClose: () => void;
  /** 有改动被应用后回调，用于刷新进度看板 */
  onApplied: () => void;
}) {
  const [advice, setAdvice] = useState('');
  const [proposal, setProposal] = useState<OutlineProposal | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ApplyOutlineResult | null>(null);

  // 关掉弹窗 / 换了一本书：回到初始状态，免得把上一本书的方案带过来
  useEffect(() => {
    if (!open) {
      setAdvice('');
      setProposal(null);
      setSelected(new Set());
      setResult(null);
    }
  }, [open, workId]);

  const proposeMutation = useMutation({
    mutationFn: () => proposeAdvisorOutline(customerId, workId, advice.trim()),
    onSuccess: (data) => {
      setProposal(data);
      setSelected(new Set(data.changes.map((c) => c.id)));
      setResult(null);
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '生成失败，过一会儿再试'));
    },
  });

  const applyMutation = useMutation({
    mutationFn: () => applyAdvisorOutline(customerId, workId, selectedOps(proposal?.changes ?? [], selected)),
    onSuccess: (data) => {
      setResult(data);
      if (data.applied > 0) onApplied();
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '应用失败'));
    },
  });

  const grouped = useMemo(() => proposal?.changes ?? [], [proposal]);
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const busy = proposeMutation.isPending || applyMutation.isPending;

  return (
    <Modal
      title="按这本书的背景生成定制大纲"
      open={open}
      width={760}
      onCancel={busy ? undefined : onClose}
      maskClosable={false}
      destroyOnHidden
      footer={
        result ? (
          <Button type="primary" onClick={onClose}>
            完成
          </Button>
        ) : proposal ? (
          <Space>
            <Button disabled={busy} onClick={() => setProposal(null)}>
              重新生成
            </Button>
            <Button
              type="primary"
              disabled={selected.size === 0 || busy}
              loading={applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
            >
              应用所选（{selected.size}）
            </Button>
          </Space>
        ) : (
          <Space>
            <Button disabled={busy} onClick={onClose}>
              取消
            </Button>
            <Button type="primary" loading={proposeMutation.isPending} onClick={() => proposeMutation.mutate()}>
              生成方案
            </Button>
          </Space>
        )
      }
    >
      {result ? (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert
            type={result.skipped.length === 0 ? 'success' : 'warning'}
            showIcon
            message={summarizeApply(result)}
            description={result.skipped.length > 0 ? '下面这几条在应用时被跳过了，大纲里已经生效的不受影响。' : undefined}
          />
          {result.skipped.length > 0 && (
            <List
              size="small"
              bordered
              dataSource={result.skipped}
              renderItem={(s) => (
                <List.Item>
                  <Space>
                    <Tag>{KIND_META[s.kind as keyof typeof KIND_META]?.label ?? s.kind}</Tag>
                    <Text>{s.reason}</Text>
                  </Space>
                </List.Item>
              )}
            />
          )}
        </Space>
      ) : proposeMutation.isPending ? (
        <Space direction="vertical" align="center" style={{ width: '100%', padding: '32px 0' }}>
          <Spin />
          <Text type="secondary">正在根据背景生成方案，大约要半分钟到一分钟，请不要关闭…</Text>
        </Space>
      ) : proposal ? (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert
            type="info"
            showIcon
            message={proposal.summary || '方案如下，请逐条确认'}
            description={
              <span>
                勾选的才会生效，不想要的取消勾选。已经聊过的题、用户自己加的题不会被改动。
                {proposal.dropped > 0 && `（另有 ${proposal.dropped} 条建议不符合规则，已自动忽略。）`}
              </span>
            }
          />
          {grouped.length === 0 ? (
            <Alert type="success" showIcon message="这份背景下，现有大纲不需要调整" />
          ) : (
            <>
              <Space>
                <Button size="small" onClick={() => setSelected(new Set(grouped.map((c) => c.id)))}>
                  全选
                </Button>
                <Button size="small" onClick={() => setSelected(new Set())}>
                  全不选
                </Button>
              </Space>
              <List
                bordered
                size="small"
                dataSource={grouped}
                renderItem={(change) => (
                  <ChangeRow change={change} checked={selected.has(change.id)} onToggle={() => toggle(change.id)} />
                )}
              />
            </>
          )}
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            根据「档案与背景」里这本书的讲述人资料，模型会提出一份大纲调整方案：删掉不相关的话题、补上贴合经历的话题、
            把问题改得更具体。<b>生成后不会自动生效</b>，你逐条勾选确认后才应用；背景里的「禁区」会作为硬约束交给模型。
          </Paragraph>
          <div>
            <Text strong>补充意见（可选）</Text>
            <TextArea
              style={{ marginTop: 6 }}
              value={advice}
              onChange={(e) => setAdvice(e.target.value)}
              maxLength={PROPOSE_ADVICE_MAX_LENGTH}
              showCount
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder="如：重点多问纺织厂的日子；少问兄弟姐妹。"
            />
          </div>
        </Space>
      )}
    </Modal>
  );
}
